'use client';

import { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { VisualizationWrapper } from './visualization-wrapper';

interface DataPoint {
  label: string;
  value: number;
  [key: string]: string | number;
}

interface GraphProps {
  element: {
    props: {
      title: string;
      type: 'line' | 'bar' | 'pie' | 'area';
      data: DataPoint[];
      xAxisLabel?: string;
      yAxisLabel?: string;
      colors?: string[];
    };
    raw?: boolean;
    visualizationId?: string;
  };
}

const DEFAULT_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

// Fallback colors if CSS variables don't work in recharts
const FALLBACK_COLORS = [
  '#8b5cf6', // violet
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
];

export function Graph({ element }: GraphProps) {
  if (!element?.props?.data?.length) {
    return <div className="p-4 border rounded-lg bg-muted/30 text-muted-foreground">Loading graph...</div>;
  }

  const { title, type, data, xAxisLabel, yAxisLabel, colors = FALLBACK_COLORS } = element.props;

  // Get all numeric keys for multi-series support
  const numericKeys = useMemo(() => {
    if (!data[0]) return ['value'];
    return Object.keys(data[0]).filter(
      key => key !== 'label' && typeof data[0][key] === 'number'
    );
  }, [data]);

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    switch (type) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 12 }}
              label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: 0 } : undefined}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            {numericKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ fill: colors[index % colors.length], strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 12 }}
              label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: 0 } : undefined}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            {numericKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 12 }}
              label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: 0 } : undefined}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            {numericKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.3}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius="80%"
              label={(entry) => `${entry.name}: ${((entry.percent ?? 0) * 100).toFixed(0)}%`}
              labelLine={{ stroke: 'hsl(var(--muted-foreground))' }}
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--popover))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
          </PieChart>
        );

      default:
        return <div className="p-4 text-muted-foreground">Unknown graph type: {type}</div>;
    }
  };

  const content = (
    <div className="w-full h-full p-4">
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );

  // Raw mode: render without wrapper for canvas
  if (element.raw) {
    return <div className="w-full h-full">{content}</div>;
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={500}
      exportData={{
        type: 'graph',
        content: element.props
      }}
      visualizationId={element.visualizationId}
    >
      <div className="w-full p-4">
        <ResponsiveContainer width="100%" height={350}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </VisualizationWrapper>
  );
}
